import LegalPage, { Section, SubSection } from "@/components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      metaTitle="AleaEdge Privacy Policy | How We Handle Your Data"
      metaDescription="How AleaEdge collects, uses, and protects your personal data — accounts, billing via Stripe, analytics, cookies, AI-generated content, and your rights."
      canonical="/privacy"
      intro="AleaEdge ('we', 'us', 'our') respects your privacy. This policy explains what we collect, why we collect it, how we use it, and the choices you have. We do not sell your personal data."
    >
      <Section title="1. Information We Collect">
        <SubSection title="Account information">
          <p>When you create an account we collect your email address, an authentication identifier, and any profile details you choose to provide. We use a secure managed authentication backend (Supabase) to store credentials.</p>
        </SubSection>
        <SubSection title="Billing information">
          <p>Subscription and one-time purchase payments are processed by Stripe. We never see or store your full card number. We retain a customer reference, subscription status, plan, and transaction metadata required to operate your account.</p>
        </SubSection>
        <SubSection title="Usage and analytics">
          <p>We use Google Analytics and first-party event logging to understand how the product is used — pages visited, picks viewed, clicks, device type, and approximate location derived from IP. Analytics data is aggregated and used to improve the service.</p>
        </SubSection>
        <SubSection title="Cookies and similar technologies">
          <p>We use strictly necessary cookies for authentication and session management, and analytics cookies to measure performance. You can control cookies through your browser settings; disabling them may break parts of the product.</p>
        </SubSection>
        <SubSection title="Communications">
          <p>If you contact support, submit feedback, or reply to an email, we retain that correspondence to provide assistance and improve the service.</p>
        </SubSection>
      </Section>

      <Section title="2. How We Use Information">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Operate, secure, and maintain your account and subscription.</li>
          <li>Deliver Top 5 picks, parlays, AI-generated analysis, and related content.</li>
          <li>Process payments, prevent fraud, and comply with legal obligations.</li>
          <li>Send transactional email (receipts, password resets, account notices).</li>
          <li>Send marketing email about features and offers — only if you opt in. You can unsubscribe at any time.</li>
          <li>Measure product performance and improve future versions.</li>
        </ul>
      </Section>

      <Section title="3. AI-Generated Content">
        <p>Some analysis and reasoning shown in AleaEdge is generated by large language models. We may send anonymized inputs (such as game data and odds) to model providers to produce that analysis. We do not send your account credentials or payment data to AI providers.</p>
      </Section>

      <Section title="4. Third-Party Services">
        <p>We share the minimum data needed with trusted processors:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="text-foreground font-medium">Stripe</span> — payment processing and subscription management.</li>
          <li><span className="text-foreground font-medium">Supabase</span> — database, authentication, file storage.</li>
          <li><span className="text-foreground font-medium">Google Analytics</span> — usage measurement.</li>
          <li><span className="text-foreground font-medium">Email delivery providers</span> — transactional and marketing email.</li>
        </ul>
        <p>Each provider operates under its own privacy and security commitments.</p>
      </Section>

      <Section title="5. Data Retention">
        <p>We retain account data for as long as your account is active and for a reasonable period afterwards to comply with tax, accounting, and dispute-resolution obligations. Backups are rotated on a fixed schedule.</p>
      </Section>

      <Section title="6. Security">
        <p>We use TLS in transit, encrypted storage at rest, role-based database access, and least-privilege server policies. No system is perfectly secure — please use a strong, unique password and enable any available account protections.</p>
      </Section>

      <Section title="7. Your Rights">
        <p>Depending on your jurisdiction you may have the right to access, correct, export, or delete your personal data, and to object to certain processing. To exercise these rights, email <a href="mailto:support@aleaedge.com" className="text-primary hover:underline">support@aleaedge.com</a>. We will respond within a reasonable timeframe.</p>
      </Section>

      <Section title="8. Children">
        <p>AleaEdge is not directed to anyone under the legal gambling age in their jurisdiction (18+ or 21+ where applicable). We do not knowingly collect data from minors.</p>
      </Section>

      <Section title="9. International Users">
        <p>Our service is operated from the United States. By using AleaEdge you consent to your information being processed in the United States and other jurisdictions where our processors operate.</p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>We may update this policy from time to time. Material changes will be communicated by email or in-product notice. Continued use of the service after the effective date constitutes acceptance.</p>
      </Section>

      <Section title="11. Contact">
        <p>Privacy questions or requests: <a href="mailto:support@aleaedge.com" className="text-primary hover:underline">support@aleaedge.com</a>.</p>
      </Section>
    </LegalPage>
  );
}
